import { useMemo, useState } from "react";
import {
  extractLinksFromEmailToImage,
  isStartOfTextRtl,
} from "../../utils/utilsFunctions/functions";
import { splitEmailBodyForDisplay } from "../../utils/emailBodyDisplay";
import { sanitizeEmailHtml } from "../../utils/sanitizeEmailHtml";

function stripTags(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
}

export function EmailMessageBody({ content }: { content: string }) {
  const [showQuoted, setShowQuoted] = useState(false);

  const { mainHtml, quotedHtml, hasQuoted, isRtl } = useMemo(() => {
    const withImages = extractLinksFromEmailToImage(content);
    const split = splitEmailBodyForDisplay(withImages);
    const main = sanitizeEmailHtml(split.main);
    const quotedRaw = split.quoted ? sanitizeEmailHtml(split.quoted) : null;

    return {
      mainHtml: main,
      quotedHtml: quotedRaw,
      hasQuoted: Boolean(quotedRaw?.trim()),
      isRtl: isStartOfTextRtl(stripTags(main) || stripTags(withImages)),
    };
  }, [content]);

  if (!mainHtml && !quotedHtml) {
    return <p className="text-sm italic text-slate-500">No message content</p>;
  }

  return (
    <div className="space-y-3">
      {mainHtml ? (
        <div
          className={`prose prose-sm max-w-none text-slate-800 [&_a]:break-all ${
            isRtl ? "text-right" : ""
          }`}
          dir={isRtl ? "rtl" : "auto"}
          dangerouslySetInnerHTML={{ __html: mainHtml }}
        />
      ) : null}

      {hasQuoted && quotedHtml ? (
        <div>
          <button
            type="button"
            onClick={() => setShowQuoted((prev) => !prev)}
            className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
          >
            {showQuoted ? "Hide quoted history" : "Show quoted history"}
          </button>
          {showQuoted ? (
            <div
              className="mt-2 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/80 p-3 text-xs leading-relaxed text-slate-600 [&_a]:break-all"
              dir="auto"
              dangerouslySetInnerHTML={{ __html: quotedHtml }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
